import { useMetadataApisByType } from "@hooks/useMetadataApisByType";
import i18n from "@i18n/i18n";
import { Alert, Button, Card, CardContent, Container, Grid, MenuItem, Select, Snackbar, TextField, Tooltip, Typography, } from "@mui/material";
import { observer } from "mobx-react";
import { useState } from "react";
import MetaDataGrid from "./MetaDataGrid";
import BenchmarkCatalogDialog from "./BenchmarkCatalogDialog";

const RebarMaterialsPage = observer(() => {
  const optionsFromI18n = i18n.t("rebar-material-tab.options", {
    returnObjects: true,
  }) as Record<string, string>;

  const TYPE_OPTIONS = Object.entries(optionsFromI18n).map(([key, label]) => ({
    key,
    label,
  }));

  const normalize = (str: string) =>
    str.toLowerCase().replace(/[-_]/g, " ").trim();

  const getKeyFromLabel = (label: string): string | undefined => {
    const normalizedLabel = normalize(label);
    return Object.entries(optionsFromI18n).find(([, val]) => {
      return normalize(val) === normalizedLabel;
    })?.[0];
  };

  const [typeLabel, setTypeLabel] = useState<string>(
    TYPE_OPTIONS[0]?.label || ""
  );
  const [valueInput, setValueInput] = useState("");
  const [duplicateError, setDuplicateError] = useState<string | null>(null);
  const [catalogDialogOpen, setCatalogDialogOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const currentTypeKey = getKeyFromLabel(typeLabel);
  const { useMetadataAdd, useData } = useMetadataApisByType(
    currentTypeKey || ""
  );

  const isModelFamily =
    currentTypeKey?.toLowerCase() === "model-family" ||
    currentTypeKey?.toLowerCase() === "modelfamily";

  const isBenchmarkCatalog = currentTypeKey?.toLowerCase() === "benchmark-catalog";

  const handleAddClick = async () => {
    if (!valueInput || !currentTypeKey) return;

    const normalizedInput = valueInput.trim().toLowerCase();

    const existingValues = ((useData?.data ?? []) as any[]).map((item: any) => {
      const value = item?.name || item?.value || item;
      return typeof value === "string" ? value.trim().toLowerCase() : value;
    });

    const isDuplicate = existingValues.includes(normalizedInput);

    if (isDuplicate) {
      setDuplicateError(
        i18n.t("rebar-material-tab.duplicate-error", { value: valueInput })
      );
      return;
    }

    try {
      await useMetadataAdd.mutateAsync({
        value: valueInput,
      });

      await useData.refetch?.();
      setDuplicateError(null);
      setSuccessMessage(i18n.t("rebar-material-tab.add-success", { value: valueInput }));
    } catch (err) {
      console.error("Error adding material:", err);
    } finally {
      setValueInput("");
    }
  };

  const getPlaceholderText = () => `Add ${typeLabel}`;

  return (
    <>
    <Container maxWidth={false} sx={{ height: "100%" }}>
      <Grid
        mt={2}
        container
        spacing={2}
        sx={{ display: "flex", flexDirection: "column", height: "100%" }}
      >
        <Grid size={12} sx={{ display: "flex", flexGrow: 0 }}>
          <Typography variant="h6" fontWeight="bold">
            {i18n.t("rebar-material-tab.rebar-header")}
          </Typography>
        </Grid>

        <Grid size={12} sx={{ display: "flex", flexGrow: 0 }}>
          <Card elevation={3}>
            <CardContent sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                <Select
                  value={typeLabel}
                  onChange={(e) => {
                    setTypeLabel(e.target.value);
                    setValueInput("");
                    setDuplicateError(null);
                  }}
                  sx={{ width: 220 }}
                >
                  {TYPE_OPTIONS.map(({ key, label }) => (
                    <MenuItem key={key} value={label}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>

                {/* Hide the text-field + add button for Benchmark Catalog; use the dedicated dialog instead */}
                {!isBenchmarkCatalog && (
                  <>
                    <TextField
                      fullWidth
                      placeholder={getPlaceholderText()}
                      value={valueInput}
                      onChange={(e) => setValueInput(e.target.value)}
                      variant="outlined"
                      sx={{ flexGrow: 1 }}
                      error={!!duplicateError}
                    />

                    <Tooltip title={i18n.t("rebar-material-tab.add-button.tooltip")}>
                      <Button
                        variant="outlined"
                        sx={{ verticalAlign: "center", paddingTop: "10px" }}
                        onClick={handleAddClick}
                      >
                        {i18n.t("rebar-material-tab.add-button.label")}
                      </Button>
                    </Tooltip>
                  </>
                )}

                {isBenchmarkCatalog && (
                  <Button
                    variant="outlined"
                    onClick={() => setCatalogDialogOpen(true)}
                  >
                    {i18n.t("benchmark-catalog-dialog.create-button-label")}
                  </Button>
                )}
              </div>

              {duplicateError && (
                <Typography color="error" variant="body2">
                  {duplicateError}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid
          size={12}
          sx={{
            display: "flex",
            flexGrow: 1,
            width: "100%",
            position: "relative",
          }}
        >
          <MetaDataGrid
            type={currentTypeKey || ""}
            showModelTypeColumn={isModelFamily}
          />
        </Grid>
      </Grid>
    </Container>

    <BenchmarkCatalogDialog
      open={catalogDialogOpen}
      onClose={() => setCatalogDialogOpen(false)}
      onSaved={() => {
        setCatalogDialogOpen(false);
        setSuccessMessage(i18n.t("benchmark-catalog-dialog.save-success"));
      }}
    />
    <Snackbar
      open={!!successMessage}
      autoHideDuration={3000}
      onClose={() => setSuccessMessage(null)}
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
    >
      <Alert severity="success" variant="filled" onClose={() => setSuccessMessage(null)}>
        {successMessage}
      </Alert>
    </Snackbar>
    </>
  );
});

export default RebarMaterialsPage;